import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import { selectUserPrompts } from 'Store/profile/selectors';
import { Prompt } from 'Store/profile/user.types';

import { trackEvent } from 'Store/sparks/actions';
import {
  DYNAMIC_UI_EDIT,
  Type as DynamicUIEditType,
} from 'Store/sparks/dynamicUI/events/dynamicUIEdit';
import {
  Action,
  EDIT_PROFILE_INTERACT,
  Type as EditProfileInteractType,
  Subtype,
  type AppSourceType,
  type PromptIdType,
} from 'Store/sparks/profile/events/editProfileInteract';

type TrackEditPromptParams = {
  id: PromptIdType;
  source: AppSourceType;
  now: string | null;
  prev: string | null;
};

type PromptAnalytics = {
  id: PromptIdType;
  answer_text: string | null;
  question_text?: string;
  image_url?: string;
};

/**
 * Processes an array of prompt objects and returns a formatted string (sorted by id)
 * @param {PromptAnalytics[]} prompts - Array of prompt objects, each containing an "id" and "answerText"
 * @returns {string} - A formatted string of the prompts in the form "prompt_id,prompt_answer;prompt_id,prompt_answer;..."
 * @example
 * [
 *   {
 *     "id": "pro_2",
 *     "answer_text": "answer-pro_2",
 *   },
 *   {
 *     "id": "pro_65",
 *     "answer_text": "answer-pro_65",
 *   },
 *   {
 *     "id": "pro_7",
 *     "answer_text": "answer-pro_7",
 *   }
 * ]
 *
 * Output: "pro_2,answer-pro_2;pro_7,answer-pro_7;pro_65,answer-pro_65;"
 */
export const getFormattedPrompts = (prompts: PromptAnalytics[] = []): string => {
  if (!prompts.length) return '';
  return prompts
    .slice()
    .sort((a, b) => +a.id.replace(/\D/g, '') - +b.id.replace(/\D/g, ''))
    .map(({ id, answer_text }) => `${id},${answer_text}`)
    .join(';')
    .concat(';');
};

/**
 * Edits the list of prompts based on the provided id and values
 * @param {PromptIdType} id - The id of the prompt to be edited
 * @param {string | null} value - The new value for the prompt's answer_text. If null, the prompt will be removed.
 * @param {string | null} prevValue - The previous value of the prompt's answer_text. If null, a new prompt will be added.
 * @param {Prompt[]} prompts - The current list of prompts
 * @returns {PromptAnalytics[]} - The updated list of prompts for the analytics purpose
 */
export const editPrompts = (
  id: PromptIdType,
  value: string | null,
  prevValue: string | null,
  prompts: Prompt[] = []
): PromptAnalytics[] => {
  const result = cloneDeep(prompts);
  // Add a prompt
  if (prevValue === null) {
    return [...result, { id, answer_text: value }];
  }
  // Remove a prompt
  if (value === null) {
    return result.filter((prompt) => prompt.id !== id);
  }
  // Edit a prompt
  return result.map((prompt) => (prompt.id === id ? { ...prompt, answer_text: value } : prompt));
};

const useAnalytics = () => {
  const dispatch = useDispatch();
  const { prompts } = useSelector(selectUserPrompts) ?? {};

  const trackEditPrompt = useCallback(
    ({ id, now, prev, source }: TrackEditPromptParams) => {
      dispatch(
        trackEvent(EDIT_PROFILE_INTERACT, {
          action: Action.EDIT,
          appSource: source,
          now,
          prev,
          subtype: id,
          type: EditProfileInteractType.USER_PROMPTS,
        })
      );
      dispatch(
        trackEvent(DYNAMIC_UI_EDIT, {
          type: DynamicUIEditType.USER_PROMPTS,
          value: getFormattedPrompts(editPrompts(id, now, prev, prompts)),
        })
      );
    },
    [dispatch, prompts]
  );

  const trackViewPrompt = useCallback(
    (id: PromptIdType, source: AppSourceType) => {
      dispatch(
        trackEvent(EDIT_PROFILE_INTERACT, {
          action: Action.VIEW,
          appSource: source,
          subtype: id,
          type: EditProfileInteractType.USER_PROMPTS,
        })
      );
    },
    [dispatch]
  );

  const trackViewPromptList = useCallback(
    (source: AppSourceType) => {
      dispatch(
        trackEvent(EDIT_PROFILE_INTERACT, {
          action: Action.VIEW,
          appSource: source,
          subtype: Subtype.PROMPT_LIST,
          type: EditProfileInteractType.USER_PROMPTS,
        })
      );
    },
    [dispatch]
  );

  return {
    trackEditPrompt,
    trackViewPrompt,
    trackViewPromptList,
  };
};

export default useAnalytics;
